import { queryCache } from "react-query";
import decode from "jwt-decode";
export const localStorageKey = "___auth_token___";

export interface User {
  accounts: Account[];
  exp: number;
  jti: string;
}

interface Account {
  account_id: string;
  role: string;
  name?: string;
  email?: string;
  permissions: {
    [project_id: string]: string[];
  };
  account_type: string;
  status?: string;
  requested_plan?: string;
  billing_origin?: string;
  trial_started_at?: string;
}

function setUserToken(token: string) {
  window.localStorage.setItem(localStorageKey, token);
}

function readStoredUser(): User | null {
  const token = window.localStorage.getItem(localStorageKey);
  if (!token) {
    return null;
  }
  try {
    // account_type is refreshed from /auth/session on load and when the tab
    // regains focus, so a downgrade or upgrade is not stuck until the next login.
    return decode<User>(token);
  } catch {
    return null;
  }
}

async function getUser() {
  return readStoredUser();
}

function getUserToken() {
  return window.localStorage.getItem(localStorageKey)!;
}

async function hasRoleAccess(role: string) {
  const user = await getUser();
  // filter() always returns an array, so the previous !== undefined check
  // granted every role to anyone holding a token.
  return !!user?.accounts?.some((account) => account.role === role);
}

function isSuperadmin(user: User | null) {
  return !!user?.accounts?.some((account) => account.role === "superadmin");
}

function logout() {
  window.localStorage.removeItem(localStorageKey);
  queryCache.clear();
}

export {
  getUser,
  hasRoleAccess,
  isSuperadmin,
  logout,
  readStoredUser,
  setUserToken,
  getUserToken,
};
